import type { ReactNode } from 'react';
import { Mail, MapPin, Phone } from 'lucide-react';
import { LineaLaser } from '../layout/LineaLaser';
import { BRAND_WORDMARK } from '../../lib/brand';
import { BRANDING, brandingLocalita } from '../../lib/branding';
import { INFORMATIVA_PRIVACY } from './informativa';

/**
 * La cornice delle pagine pubbliche di Pixel, con gli stessi elementi del
 * gestionale:
 *   - lo sfondo della pagina di accesso (nero con gli aloni nei colori del
 *     logo), fermo mentre si scorre;
 *   - l'intestazione bianca a quadretti con la linea laser rossa;
 *   - un pie' di pagina con i dati dell'azienda.
 *
 * Le classi .liquid-glass-background e .header-line-texture fissano da sole
 * `position: relative`: fisso e appiccicato si impostano con `style`, che le
 * scavalca.
 */
export function CornicePubblica({ children }: { children: ReactNode }) {
  return (
    <div className="relative flex min-h-[100dvh] flex-col">
      <div aria-hidden="true" className="liquid-glass-background" style={{ position: 'fixed', inset: 0, zIndex: 0 }} />

      <header className="header-line-texture z-30" style={{ position: 'sticky', top: 0 }}>
        <div className="relative z-10 mx-auto flex max-w-3xl items-center px-4 py-3">
          <img src={BRAND_WORDMARK} alt={BRANDING.nomeBreve} className="h-11 w-auto sm:h-14" />
        </div>
        <LineaLaser />
      </header>

      <main className="relative z-10 mx-auto w-full max-w-3xl flex-1 px-3 pb-16 pt-8 sm:px-4">{children}</main>

      <PiePagina />
    </div>
  );
}

function PiePagina() {
  const indirizzo = [BRANDING.indirizzo, brandingLocalita()].filter(Boolean).join(' - ');
  return (
    <footer className="relative z-10 border-t border-white/10 bg-black/60 px-4 py-10 text-center text-gray-100 backdrop-blur-md">
      {/* La linea laser anche qui, sul bordo alto: chiude la pagina come l'intestazione la apre */}
      <div className="absolute inset-x-0 top-0 h-0">
        <LineaLaser />
      </div>
      <p className="text-xl font-semibold text-white">{BRANDING.ragioneSociale}</p>
      <hr className="mx-auto my-5 max-w-sm border-white/20" />
      <ul className="space-y-4 text-lg">
        {indirizzo && (
          <li className="flex items-center justify-center gap-2">
            <MapPin className="h-5 w-5 flex-shrink-0 text-primary-400" aria-hidden="true" />
            {indirizzo}
          </li>
        )}
        {BRANDING.telefono && (
          <li>
            <a href={`tel:${BRANDING.telefono.replace(/[^\d+]/g, '')}`} className="inline-flex items-center gap-2 font-semibold hover:underline">
              <Phone className="h-5 w-5 text-primary-400" aria-hidden="true" />
              {BRANDING.telefono}
            </a>
          </li>
        )}
        {BRANDING.email && (
          <li>
            <a href={`mailto:${BRANDING.email}`} className="inline-flex items-center gap-2 font-semibold hover:underline">
              <Mail className="h-5 w-5 text-primary-400" aria-hidden="true" />
              {BRANDING.email}
            </a>
          </li>
        )}
      </ul>
      <hr className="mx-auto my-5 max-w-sm border-white/20" />
      <a href={INFORMATIVA_PRIVACY} target="_blank" rel="noopener noreferrer" className="text-lg font-semibold hover:underline">
        Informativa Privacy
      </a>
    </footer>
  );
}
